import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, MessageCircle } from "lucide-react";
import { useSite } from "@/context/SiteContext";

const navKeys = ["home", "about", "treatments", "conditions", "journey", "international", "science", "faq", "contact"];
const anchorMap = {
  home: "#top", about: "#about", treatments: "#treatments", conditions: "#conditions",
  journey: "#method", international: "#international", science: "#science", faq: "/faq", contact: "/contact",
};

const Navbar = () => {
  const { t, lang, changeLang, openBooking, openWhatsApp } = useSite();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (hash) => {
    setOpen(false);
    if (hash === "/faq" && lang === "es") { navigate("/es/preguntas-frecuentes"); return; }
    if (hash === "/contact" && lang === "es") { navigate("/es/contacto"); return; }
    if (hash.startsWith("/")) { navigate(hash); return; }
    if (hash === "#top") {
      window.__lenis ? window.__lenis.scrollTo(0) : window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    const el = document.querySelector(hash);
    if (el) {
      window.__lenis ? window.__lenis.scrollTo(el, { offset: -80 }) : el.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <>
      <motion.header
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
        className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,box-shadow,padding] duration-500 ${
          scrolled ? "bg-white/85 backdrop-blur-xl border-b border-navy/5 py-3 shadow-[0_4px_30px_rgba(15,37,55,0.05)]" : "bg-transparent py-5"
        }`}
        data-testid="navbar"
      >
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16 flex items-center justify-between">
          <button onClick={() => go("#top")} className="flex items-center gap-3 group shrink-0 mr-4 text-left" data-testid="logo-btn">
            <img src="/logo-mark.png" alt="Revive Regenerative Medical Clinic" className="h-9 sm:h-11 w-auto drop-shadow-sm" />
            <span className="flex flex-col leading-none">
              <span className="font-serif text-2xl tracking-tight text-gold">Revive</span>
              <span className={`hidden sm:block font-sans text-[9px] sm:text-[10px] tracking-[0.22em] uppercase mt-1 transition-colors duration-500 ${scrolled ? "text-navy/55" : "text-white/70"}`}>
                Regenerative Medical Clinic
              </span>
            </span>
          </button>

          <nav className="hidden xl:flex items-center gap-5 ml-auto mr-5">
            {navKeys.map((k) => (
              <button
                key={k}
                onClick={() => go(anchorMap[k])}
                className={`text-[13px] font-sans whitespace-nowrap relative after:absolute after:left-0 after:-bottom-1 after:h-px after:w-0 after:bg-gold hover:after:w-full after:transition-all after:duration-300 transition-colors ${scrolled ? "text-navy/70 hover:text-navy" : "text-white/80 hover:text-white"}`}
                data-testid={`nav-${k}`}
              >
                {t.nav[k]}
              </button>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <div className={`hidden sm:flex items-center text-sm font-sans border rounded-full overflow-hidden transition-colors duration-500 ${scrolled ? "border-navy/15" : "border-white/30"}`}>
              {["en", "es"].map((l) => (
                <button
                  key={l}
                  onClick={() => changeLang(l)}
                  className={`px-3 py-1.5 transition-colors duration-300 ${lang === l ? (scrolled ? "bg-navy text-white" : "bg-white text-navy") : (scrolled ? "text-navy/60 hover:text-navy" : "text-white/70 hover:text-white")}`}
                  data-testid={`lang-${l}`}
                >
                  {l.toUpperCase()}
                </button>
              ))}
            </div>

            <button
              onClick={() => openWhatsApp()}
              className={`hidden sm:grid place-items-center w-10 h-10 rounded-full border transition-colors duration-300 hover:bg-[#25D366] hover:border-[#25D366] hover:text-white ${scrolled ? "border-navy/15 text-navy" : "border-white/30 text-white"}`}
              aria-label="WhatsApp"
              data-testid="nav-whatsapp"
            >
              <MessageCircle size={18} />
            </button>

            <button
              onClick={() => openBooking()}
              className={`hidden lg:inline-flex items-center rounded-full text-sm font-medium px-5 py-2.5 transition-colors duration-300 ${scrolled ? "bg-navy text-white hover:bg-royal" : "bg-white text-navy hover:bg-champagne"}`}
              data-testid="nav-book"
            >
              {t.nav.book}
            </button>

            <button
              onClick={() => setOpen((o) => !o)}
              className={`xl:hidden grid place-items-center w-10 h-10 rounded-full border transition-colors duration-300 ${open ? "border-navy/15 text-navy" : scrolled ? "border-navy/15 text-navy" : "border-white/30 text-white"}`}
              aria-label="Menu"
              data-testid="menu-toggle"
            >
              {open ? <X size={18} /> : <Menu size={18} />}
            </button>
          </div>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-40 bg-navy text-white xl:hidden flex flex-col pt-28 px-8 overflow-y-auto"
            data-testid="mobile-menu"
          >
            <nav className="flex flex-col gap-1">
              {navKeys.map((k, i) => (
                <motion.button
                  key={k}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 * i }}
                  onClick={() => go(anchorMap[k])}
                  className="text-left font-serif text-3xl py-2 border-b border-white/10 hover:text-champagne transition-colors"
                  data-testid={`mnav-${k}`}
                >
                  {t.nav[k]}
                </motion.button>
              ))}
            </nav>
            <div className="flex items-center gap-3 mt-8">
              {["en", "es"].map((l) => (
                <button key={l} onClick={() => changeLang(l)}
                  className={`px-4 py-2 rounded-full text-sm border ${lang === l ? "bg-champagne text-navy border-champagne" : "border-white/30 text-white/70"}`}>
                  {l.toUpperCase()}
                </button>
              ))}
            </div>
            <button onClick={() => { setOpen(false); openBooking(); }}
              className="mt-8 rounded-full bg-champagne text-navy font-medium px-6 py-4 text-lg" data-testid="mobile-menu-book">
              {t.nav.book}
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
